/**
 * TenantSwitcher — dropdown no header para trocar o tenant ativo.
 *
 * Comportamento:
 *  1. Lista os tenants acessíveis a partir de `useTenantStore.accessibleTenants`.
 *  2. Marca o tenant atual com um check à direita.
 *  3. Ao selecionar outro tenant:
 *       - chama `setActiveTenant(novo)` no store (persiste `last_tenant_id`);
 *       - chama `queryClient.invalidateQueries()` para invalidar TODO o cache
 *         (as queries tenant-escopadas levam o `tenantId` na key e serão
 *         refetchadas com o novo tenant injetado pelo interceptor Axios);
 *       - redireciona para `/` para evitar ficar em uma rota cujo recurso
 *         (`/test-cases/:id`, `/executions/:id`, etc.) não existe no novo tenant.
 *  4. Se houver apenas um tenant acessível ou nenhum, o trigger é desabilitado.
 *
 * Notas:
 *  - O interceptor Axios lê `useTenantStore.getState().activeTenant` a cada
 *    request, então não é necessário reconfigurar nada além do store.
 *  - O SSE global NÃO é reconectado aqui (baseado no JWT, escopo do usuário).
 */
import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronsUpDown, Building2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useTenantStore } from '@/store/tenant.store'
import type { Tenant } from '@/types/api'

export interface TenantSwitcherProps {
  /** Classes extras para o trigger (usadas pelo header). */
  className?: string
}

export function TenantSwitcher({ className }: TenantSwitcherProps): React.ReactElement | null {
  const activeTenant = useTenantStore((s) => s.activeTenant)
  const accessibleTenants = useTenantStore((s) => s.accessibleTenants)
  const setActiveTenant = useTenantStore((s) => s.setActiveTenant)

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Sem tenant acessível — não renderiza nada (não há o que escolher).
  if (accessibleTenants.length === 0 && !activeTenant) {
    return null
  }

  const onlyOne = accessibleTenants.length <= 1
  const label = activeTenant?.name ?? 'Selecionar tenant'

  function handleSelect(tenant: Tenant): void {
    if (activeTenant && tenant.id === activeTenant.id) return
    setActiveTenant(tenant)
    // Invalida TODO o cache — tenant-escopadas refetcham com o novo tenantId
    // e as globais (`me`, `workers`) permanecem válidas mas serão re-validadas.
    queryClient.invalidateQueries()
    // Redireciona para a raiz: rotas com recursos específicos do tenant
    // anterior (test-cases/:id, executions/:id) não fazem sentido aqui.
    navigate('/', { replace: true })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={onlyOne}
          data-testid="tenant-switcher-trigger"
          className={cn('min-w-[180px] justify-between gap-2', className)}
          aria-label={`Tenant atual: ${label}`}
        >
          <span className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="truncate" data-testid="tenant-switcher-current">
              {label}
            </span>
          </span>
          {!onlyOne ? (
            <ChevronsUpDown
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[240px]"
        data-testid="tenant-switcher-menu"
      >
        <DropdownMenuLabel>Trocar de tenant</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {accessibleTenants.map((tenant) => {
          const isActive = tenant.id === activeTenant?.id
          return (
            <DropdownMenuItem
              key={tenant.id}
              data-testid={`tenant-switcher-item-${tenant.id}`}
              onSelect={() => handleSelect(tenant)}
              className="flex items-center justify-between gap-2"
            >
              <span className="truncate">{tenant.name}</span>
              {isActive ? (
                <Check
                  className="h-4 w-4 shrink-0 text-primary"
                  aria-label="Tenant ativo"
                />
              ) : null}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
